import { getNestedProperty } from 'app/shared/utils/getNestedProperty.util';
import { EventService } from './../../../../services/event-service';
import { ClipboardService } from 'ngx-clipboard';
import { LocalStorageService } from 'angular-2-local-storage';
import { Subscription, ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';

import { ReduxService } from '../../../../services/redux.service';
import { UUIDV4 } from '../../../../shared/tools/UUIDV4';

import { InteractivityMediaService } from './interactivity-media.service';
import { IWidgetDefinition } from './../interfaces/IWidgetDefinition';
import { WidgetType } from '../enums/WidgetType';
import { IDataWidget, IAnimationData } from '../redux/timeline-data.model';
import { TimelineDataActions } from '../redux/timeline-data-actions';
import { InteractivityEditorService } from './interactivity-editor.service';
import { PillboxConfiguration } from '../../bb-pillbox/bb-pillbox.component';
import { IWidgetProperty } from '../interfaces/IWidgetProperty';
import { FilterSet } from '../../../../services/filter-set.service';
import { InteractivityWidgetSelectionService } from './interactivity-widget-selection.service';
import { IWidgetSelection } from '../interfaces/IWidgetSelection';
import { InteractivityWidgetDefinitionService } from './interactivity-widget-definition.service';
import { IWidget } from '../interfaces/IWidget';
import { DesignDimensions, InteractivityDesignDimensionsService } from './interactivity-design-dimensions.service';
import { TranslateService } from '@ngx-translate/core';
import { Mediaclip } from '../../../../interfaces/mediaclip';
import { SapiService } from '../../../../services/sapi.service';
import { StageWidget } from '../classes/StageWidget';
import { InteractivityWidgetAnimationService } from './interactivity-widget-animation.service';
import { ITrackAreaItem } from '../interfaces/ITrackAreaItem';
import { Track } from '../classes/Track';

@Injectable()
export class InteractivityWidgetService {
	public aspectRatioLockedSubject: ReplaySubject<boolean>;
	public clipBoard: { widgets?: IWidget[]; tracks?: ITrackAreaItem[] } = {
		widgets: [],
	};

	private _widgetDefinitions: IWidgetDefinition[];
	private widgetSelection: IWidgetSelection;

	private duration: number;

	private _designWidth = 768;
	private _designHeight = 432;

	private _aspectRatioLocked = false;
	private _aspectRatio = 0;
	private _stageAspectRatio: number;
	private _widgetType: string;
	private activeArea: string;

	private subscriptions = new Subscription();

	constructor(
		private reduxService: ReduxService,
		private translate: TranslateService,
		private sapiService: SapiService,
		private interactivityEditorService: InteractivityEditorService,
		private interactivityWidgetSelectionService: InteractivityWidgetSelectionService,
		private interactivityWidgetDefinitionService: InteractivityWidgetDefinitionService,
		private interactivityDesignDimensionsService: InteractivityDesignDimensionsService,
		private interactivityWidgetAnimationService: InteractivityWidgetAnimationService,
		private mediaService: InteractivityMediaService,
		private localStorageService: LocalStorageService,
		private eventService: EventService,
		private clipboardService: ClipboardService
	) {

		this._widgetDefinitions = [];
		this.aspectRatioLockedSubject = new ReplaySubject<boolean>(1);

		this.subscriptions.add(this.interactivityWidgetDefinitionService.widgetDefinitionsSubject.subscribe((widgetDefinitions) => {
			this._widgetDefinitions = widgetDefinitions;
		}));

		this.subscriptions.add(this.interactivityEditorService.durationSubject.subscribe((duration: number) => {
			this.duration = duration;
		}));

		this.subscriptions.add(this.interactivityEditorService.activeAreaSubject.subscribe((activeArea) => {
			this.activeArea = activeArea;
		}));

		this.subscriptions.add(this.interactivityDesignDimensionsService.designDimensionsSubject.subscribe((designDimensions: DesignDimensions) => {
			this.designDimensionsChanged(designDimensions);
		}));

		this.subscriptions.add(this.eventService.clipboardPasteSubject.subscribe((pasteText) => {
			this.onNativeClipboardPaste(pasteText);
		}));

		this.subscriptions.add(this.interactivityWidgetSelectionService.widgetSelectionSubject.subscribe((data: IWidgetSelection) => {
			this.widgetSelection = data;

			// aspect ratio locking
			let aspectRatio_numerator = 0;
			let aspectRatio_denominator = -1;
			for (let i = 0, l = this.widgetSelection.commonProperties.length; i < l; i++) {
				const prop: IWidgetProperty = this.widgetSelection.commonProperties[i];
				if (prop.key === 'width') {
					aspectRatio_numerator = (prop.value !== '__...__') ? parseFloat(prop.value) : 0;
				}
				if (prop.key === 'height') {
					aspectRatio_denominator = (prop.value !== '__...__') ? parseFloat(prop.value) : -1;
				}
			}
			this.aspectRatio = (aspectRatio_numerator / aspectRatio_denominator) * (this._designWidth / this._designHeight);

			if (this.widgetSelection && this.widgetSelection.widgets && this.widgetSelection.widgets.length === 1) {
				this._widgetType = this.widgetSelection.widgets[0].type;
			} else {
				this._widgetType = null;
			}
		}));
	}

	public get aspectRatioLocked() {
		return this._aspectRatioLocked;
	}

	public set aspectRatioLocked(aspectRatioLocked: boolean) {
		this._aspectRatioLocked = aspectRatioLocked;
		if (this._widgetType) {
			this.localStorageService.set('ovp-ie-aspectLockedFor_' + this._widgetType, aspectRatioLocked);
		}
		this.aspectRatioLockedSubject.next(this._aspectRatioLocked);
	}

	public get aspectRatio() {
		return this._aspectRatio;
	}

	public set aspectRatio(aspectRatio: number) {
		this._aspectRatio = aspectRatio;
	}

	public getDuplicateName(name: string): string {
		const widgetNames = this.interactivityEditorService.widgetsAsOptions.map(widgetOption => widgetOption.label);
		let newName = name;
		let i = 2;

		const nameParts = newName.split(' copy');
		const num = parseFloat(nameParts[1]);

		if (!newName.endsWith(' copy') && num) {
			// i.e. 'widget label copy 4';
			newName = nameParts[0] + ' copy';
		} else if (!newName.endsWith(' copy')) {
			newName = nameParts[0] + ' copy';
		}

		while (widgetNames.indexOf(newName + ' ' + i) !== -1 && i < 500) {
			i++;
		}
		newName = newName + ' ' + i;

		return newName;
	}

	public checkForAnimations(animations: IAnimationData[], legend: string) {
		const a = animations.find((animation) => animation.legend === legend);
		return a && a.data && a.data.length;
	}

	public copy(widgets: IWidget[]): void {
		this.clipBoard.widgets = widgets;
		this.clipBoard.tracks = widgets.map(w => this.interactivityEditorService.getTrackByWidgetId(w.id));
		const clipboardData = JSON.stringify({
			IDataWidgets: this.clipBoard.widgets.map(w => w && w.data),
			IDataTrackAreaItems: this.clipBoard.tracks.map(t => t && t.data),
		});
		this.clipboardService.copyFromContent(clipboardData);
	}

	public paste(deltas?: { xpos?: string; ypos?: string }): void {
		this.duplicateWidgets(this.clipBoard.widgets, deltas, true);
	}

	onNativeClipboardPaste(text: string) {
		if (this.activeArea === 'central-area' || this.activeArea === 'bottom-area') {
			let pasteObject;
			try {
				pasteObject = JSON.parse(text);
			} catch (e) { }
			if (
				pasteObject && pasteObject.IDataWidgets && pasteObject.IDataWidgets.length && pasteObject.IDataTrackAreaItems
				&& pasteObject.IDataTrackAreaItems.length
			) {
				this.clipBoard.tracks = pasteObject.IDataTrackAreaItems.map(dataTrack => new Track(dataTrack));
				this.clipBoard.widgets = pasteObject.IDataWidgets.map(dataWidget => this.interactivityEditorService.createWidgetFromData(dataWidget));
				this.duplicateWidgets(this.clipBoard.widgets);
			} else {
				if (this.clipBoard && this.clipBoard.widgets && this.clipBoard.widgets.length) {
					const newIds = this.duplicateWidgets(this.clipBoard.widgets, null, true);
					setTimeout(() => {
						this.interactivityWidgetSelectionService.clearSelectedWidgets();
						this.interactivityWidgetSelectionService.addSelectedWidgetByIdMulti(newIds);
					}, 50);
				}
			}
		}
	}

	public duplicateWidget(widget: IWidget, toPosition?: { xpos: string | number; ypos: string | number }): string {
		const newId = 'w' + UUIDV4.getUniqueId();
		this.reduxService.dispatch({
			type: TimelineDataActions.DUPLICATE_WIDGET,
			widgetOverrides: {
				id: newId,
				label: this.getDuplicateName(widget.label)
			},
			toPosition,
			fromWidgetId: widget.id,
			description: 'Duplicated widget',
			targetIndex: null,
			intoTrackGroupId: null
		});
		return newId;
	}

	public duplicateWidgets(widgets: IWidget[], pos?: { xpos?: string; ypos?: string }, deltaPos?: boolean): string[] {
		const newIds: string[] = [];
		const newWidgetsActions: any[] = [];

		widgets.forEach((widget) => {
			const newId = 'w' + UUIDV4.getUniqueId();
			newIds.push(newId);
			let toPosition: any = {};
			if (pos && deltaPos) {
				toPosition.xpos = parseFloat(widget.config.xpos) + parseFloat(pos.xpos) + '%';
				toPosition.ypos = parseFloat(widget.config.ypos) + parseFloat(pos.ypos) + '%';
			} else if (pos && Object.keys(pos).length) {
				toPosition = pos;
			} else {
				toPosition.xpos = parseFloat(widget.config.xpos) + 5 + '%';
				toPosition.ypos = parseFloat(widget.config.ypos) + 5 + '%';
			}

			const track = this.clipBoard.tracks && this.clipBoard.tracks.find((t: ITrackAreaItem) => t && t.widgetId === widget.id);

			newWidgetsActions.push({
				type: TimelineDataActions.DUPLICATE_WIDGET,
				widgetOverrides: {
					id: newId,
					label: this.getDuplicateName(widget.label)
				},
				widgetData: widget.data,
				trackData: track && track.data,
				toPosition,
				fromWidgetId: widget.id,
				description: 'Duplicated widget',
				targetIndex: null,
				intoTrackGroupId: null
			});
		});
		this.reduxService.dispatch({
			type: TimelineDataActions.DUPLICATE_WIDGETS,
			duplicateActions: newWidgetsActions,
			deltas: pos,
			description: `Duplicated ${newWidgetsActions.length} widgets`,
			targetIndex: null,
			intoTrackGroupId: null
		});

		return newIds;
	}

	public createWidget(type: WidgetType, overrideConfig: any = null, targetIndex: number = null, groupId: string = null, label: string = null): void {

		let config: any = this.getDefaultConfig(type);
		if (overrideConfig != null) {
			config = Object.assign({}, config, overrideConfig);
		}

		if (type && type.indexOf('form') !== -1) {
			config.formId = '1';
		}

		const newWidget: IDataWidget = {
			id: 'w' + UUIDV4.getUniqueId(),
			status: '',
			widgetdefinitiontype: type,
			widgetdefinitionid: this.getDefinitionId(type),
			label: label || this.translate.instant('WIDGET_TYPE_' + type.toUpperCase()),
			statesets: [{
				name: 'default',
				states: [
					{ name: 'default', config },
					{ name: 'mouseover', config: this.getDefaultConfig(type, 'mouseover') },
					{ name: 'mousedown', config: this.getDefaultConfig(type, 'mousedown') },
					{ name: 'toggled', config: this.getDefaultConfig(type, 'toggled') },
					{ name: 'toggledmouseover', config: this.getDefaultConfig(type, 'toggledmouseover') },
					{ name: 'toggledmousedown', config: this.getDefaultConfig(type, 'toggledmousedown') }]
			}
			],
			grouping: [],
			animations: []
		};

		let outPoint: number;
		if (type === 'chapter') {
			outPoint = this.mediaService.currentTime;
			groupId = null;
			targetIndex = null;
		} else {
			outPoint = this.duration;
		}

		this.reduxService.dispatch({
			type: TimelineDataActions.ADD_WIDGET,
			widget: newWidget,
			description: 'Create new ' + type + ' widget',
			ioPoint: { inPoint: this.mediaService.currentTime, outPoint },
			targetIndex,
			intoTrackGroupId: groupId
		});
		setTimeout(() => {
			this.interactivityWidgetSelectionService.clearSelectedWidgets();
			this.interactivityWidgetSelectionService.addSelectedWidgetById(newWidget.id, { area: 'generic' }, true);
		});
	}

	distribute(direction: 'v' | 'h') {
		const widgetsChangeData: any[] = [];
		const pos = direction === 'h' ? 'xpos' : 'ypos';
		const dim = direction === 'h' ? 'width' : 'height';
		let min: number; let max: number; let totalSize = 0; const num = this.widgetSelection.widgets.length;

		this.widgetSelection.widgets.forEach((widget) => {
			const offset = parseFloat(widget.config[pos]);
			const d = parseFloat(widget.config[dim]);
			totalSize += d;
			const wMax = offset + d;
			if (!min || offset < min) {
				min = offset;
			}
			if (!max || wMax > max) {
				max = wMax;
			}
		});

		const sortedWidgets = this.widgetSelection.widgets.sort((widgedA, widgetB) => {
			const offsetA = parseFloat(widgedA.config[pos]);
			const offsetB = parseFloat(widgetB.config[pos]);
			return offsetA - offsetB;
		});
		const space = max - min;
		const spaceBetween = (space - totalSize) / (num - 1);
		const newWidgetPositions: number[] = [];

		sortedWidgets.forEach((widget, i) => {
			let newWidgetStart = min;
			if (i > 0) {
				const prevWidget = sortedWidgets[i - 1];
				newWidgetStart = newWidgetPositions[i - 1] + parseFloat(prevWidget.config[dim]) + spaceBetween;
			}
			newWidgetPositions.push(newWidgetStart);
			const newConfig: any = {};
			newConfig[pos] = newWidgetStart + '%';

			if (this.checkForAnimations(widget.animations, 'xpos,ypos')) {
				this.interactivityWidgetAnimationService.addOrUpdateKeyframe(widget.id, newConfig.xpos + ',' + newConfig.ypos, 'xpos,ypos');
			} else {
				widgetsChangeData.push({
					id: widget.id,
					config: newConfig
				});
			}
		});
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
			widgets: widgetsChangeData,
			stateSet: this.interactivityEditorService.activeStateSet,
			state: this.interactivityEditorService.activeState,
			description: 'Changed position of ' + this.widgetSelection.widgets.length + ' widget(s)'
		});
	}

	align(position: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') {
		const widgetsChangeData: any[] = [];
		const pos = position === 'left' || position === 'center' || position === 'right' ? 'xpos' : 'ypos';
		const dim = position === 'left' || position === 'center' || position === 'right' ? 'width' : 'height';
		let min: number; let max: number;
		// let totalSize: number = 0;

		this.widgetSelection.widgets.forEach((widget) => {
			const offset = parseFloat(widget.config[pos]);
			const d = parseFloat(widget.config[dim]);
			// totalSize += d;
			const wMax = offset + d;
			if (!min || offset < min) {
				min = offset;
			}
			if (!max || wMax > max) {
				max = wMax;
			}
		});

		const space = max - min;
		const center = min + (space / 2);

		this.widgetSelection.widgets.forEach((widget, i) => {
			let newWidgetPos;
			if (position === 'left' || position === 'top') {
				newWidgetPos = min;
			} else if (position === 'center' || position === 'middle') {
				newWidgetPos = center - (parseFloat(widget.config[dim]) / 2);
			} else if (position === 'right' || position === 'bottom') {
				newWidgetPos = max - parseFloat(widget.config[dim]);
			}
			const newConfig: any = {};
			newConfig[pos] = newWidgetPos + '%';

			if (this.checkForAnimations(widget.animations, 'xpos,ypos')) {
				this.interactivityWidgetAnimationService.addOrUpdateKeyframe(widget.id, newConfig.xpos + ',' + newConfig.ypos, 'xpos,ypos');
			} else {
				widgetsChangeData.push({
					id: widget.id,
					config: newConfig
				});
			}

		});

		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
			widgets: widgetsChangeData,
			stateSet: this.interactivityEditorService.activeStateSet,
			state: this.interactivityEditorService.activeState,
			description: 'Changed position of ' + this.widgetSelection.widgets.length + ' widget(s)'
		});
	}

	restoreAspectRatio(widget: StageWidget) {
		let mcId = widget && (
			widget.type === 'image' && widget.getConfigValue('imageid') ||
			widget.type === 'video' && widget.getConfigValue('clipid')
		);
		if (mcId && mcId.datasource && mcId.datasource.defaultValue) {
			mcId = mcId.datasource.defaultValue;
		}

		if (mcId) {
			this.sapiService.get('commonclip', mcId).subscribe((clip: Mediaclip) => {
				const img = widget && widget.elRef && widget.elRef.nativeElement && widget.elRef.nativeElement.querySelector('img');
				let w = 0;
				let h = 0;
				if (clip.width && clip.height) {
					w = clip.width;
					h = clip.height;
				} else if (img.naturalWidth && img.naturalHeight) {
					w = img.naturalWidth;
					h = img.naturalHeight;
				}
				if (w && h) {
					const dar = h / w;
					const wPerc = parseFloat(widget.getConfigValue('width'));
					const newHeight = dar * wPerc / this._stageAspectRatio;
					this.interactivityEditorService.changeWidgetsProperty([widget], 'height', newHeight + '%');
				}
			});
		}
	}

	keepAspectRatio(changedProp: any) {
		const val = changedProp.val;
		const key = changedProp.key;
		if (this.aspectRatio <= 0) {
			return;
		} // bail out

		if (key === 'width') {
			const newHeight = val / this.aspectRatio;
			this.changeProperty({ key: 'height', val: newHeight }, false);
		} else if (key === 'height') {
			const newWidth = val * this.aspectRatio;
			this.changeProperty({ key: 'width', val: newWidth }, false);
		}
	}

	addStateProperty(propertyPath: string, state) {
		let val = getNestedProperty(this.widgetSelection.widgets[0].getConfigByState('default', 'default'), propertyPath);
		if (['textFormat/textFormat/bold', 'textFormat/textFormat/italic', 'textFormat/textFormat/underline'].includes(propertyPath)) {
			val = val === 'true' ? 'false' : 'true';
		}
		this.interactivityEditorService.setTmpState(state);
		this.interactivityEditorService.changeWidgetsProperty(this.widgetSelection.widgets, propertyPath, val);
	}

	changeProperty(changedProp: {key: string; val: any}, mayKeepAspectRatio: boolean = true, msg?: string, remove?: boolean) {
		let val = null;
		const key = changedProp.key;
		// handle aspect ratio first to keep 'translateValueForRedux' more generic
		if (this.aspectRatioLocked && mayKeepAspectRatio && (key === 'width' || key === 'height')) {
			this.keepAspectRatio(changedProp);
		}
		val = this.interactivityDesignDimensionsService.translateValueForRedux(changedProp);
		if (remove || val || val === '') {
			this.interactivityEditorService.changeWidgetsProperty(this.widgetSelection.widgets, key, remove ? undefined : val, msg);
		}
	}

	getComponentConfigFromProperty(prop: IWidgetProperty): PillboxConfiguration {
		let ctype = 'none';
		const key: string = prop.key;
		let index: string = prop.key; // default
		let prefix = '';
		let value: string = prop.value;
		let suffix = '';
		let dividerLine = false;
		let filterSet: FilterSet = null;
		let showAspectRatioLock = false;
		let aspectRatioLocked = false;

		if (prop.key === 'width') {
			// override aspectRatioLock based on LocalStorage and WidgetType (or default behaviour)
			let locked: boolean;

			if (this._widgetType && this.localStorageService.get('ovp-ie-aspectLockedFor_' + this._widgetType) !== null) {
				locked = this.localStorageService.get('ovp-ie-aspectLockedFor_' + this._widgetType);
			} else if (this._widgetType && (this._widgetType === 'image' || this._widgetType === 'video')) {
				locked = true;
			} else {
				locked = this._aspectRatioLocked;
			}

			this.aspectRatioLocked = locked; // needed to tell the rest of the components that need to know (handled in setter)

			aspectRatioLocked = locked && (this.aspectRatio > 0);
			showAspectRatioLock = true;
		}

		// maybe also use prop.type & prop.itype?
		switch (key) {
			case 'xpos': // x position
			case 'width':
				ctype = 'pillbox';
				index = key.substr(0, 1).toUpperCase(); // X, W
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = false;
				break;

			case 'ypos': // y position
			case 'height':
				ctype = 'pillbox';
				index = key.substr(0, 1).toUpperCase(); // Y, H
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = true;
				break;

			case 'color': // is the background color for rectangle widgets
			case 'backgroundcolor':
				ctype = 'pillbox_color'; // color
				index = '__color__';
				prefix = '#';
				dividerLine = false;
				break;

			case 'alpha': // background alpha
			case 'background_alpha': // background opacity
			case 'textFormat/textFormat/background_alpha': // background opacity
				ctype = 'pillbox';
				index = 'opacity';
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = '%'; // percentage
				dividerLine = false;
				break;

			case 'cornerradius': // border corner radius
				ctype = 'pillbox';
				index = '__radius__';
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = true;
				break;

			case 'bordercolor': // border color
				ctype = 'pillbox_color'; // color
				index = '__border__';
				prefix = '#';
				dividerLine = false;
				break;

			case 'borderthickness': // border thickness
				ctype = 'pillbox';
				index = '__adjunct__';
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = false;
				break;

			case 'imageid': // background image
				ctype = 'pillbox_mediaclip';
				index = '__image__';
				filterSet = [{ filters: [{ type: 'commonclip', field: 'mediatype', operator: 'is', value: 'image' }] }];
				dividerLine = false;
				break;

			case 'imageid_mouseover': // hover
				ctype = 'pillbox_mediaclip';
				index = 'HOVER';
				filterSet = [{ filters: [{ type: 'commonclip', field: 'mediatype', operator: 'is', value: 'image' }] }];
				dividerLine = true;
				break;

			case 'imageid_mousedown': // active
				ctype = 'pillbox_mediaclip';
				index = 'ACTIVE';
				filterSet = [{ filters: [{ type: 'commonclip', field: 'mediatype', operator: 'is', value: 'image' }] }];
				dividerLine = false;
				break;

			case 'imageid_toggle': // toggled
				ctype = 'pillbox_mediaclip';
				index = 'TOGGLED';
				filterSet = [{ filters: [{ type: 'commonclip', field: 'mediatype', operator: 'is', value: 'image' }] }];
				dividerLine = false;
				break;

			case 'clipid': // media clip id
				ctype = 'none';
				break;

			case 'appid':
				ctype = 'pillbox_mediaclip';
				index = '__app__';
				dividerLine = false;
				break;

			case 'textFormat/textFormat/bold':
				ctype = 'toggle';
				index = '__bold__';
				dividerLine = false;
				break;

			case 'textFormat/textFormat/italic':
				ctype = 'toggle';
				index = '__italic__';
				dividerLine = false;
				break;

			case 'textFormat/textFormat/underline':
				ctype = 'toggle';
				index = '__underline__';
				dividerLine = false;
				break;

			case 'textFormat/textFormat/align':
			case 'align':
				ctype = 'select';
				index = '__align__';
				dividerLine = true;
				break;

			case 'textFormat/textFormat/color': // font color
				index = '__color__';
				ctype = 'pillbox_color'; // color
				prefix = '#';
				dividerLine = false;
				break;

			case 'textFormat/textFormat/font': // font family
				ctype = 'pillbox_mediaclip';
				index = '__font__';
				dividerLine = false;
				break;

			case 'textFormat/textFormat/fontsize': // font size
				ctype = 'pillbox';
				index = '__adjunct__';
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = false;
				break;

			case 'textFormat/textFormat/margin': // text margin(s)
				ctype = 'pillbox_multi';
				index = '__margin__';
				if (prop.type === 'percentage' || true) { // NB || true => always!
					value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				} else {
					value = this.interactivityDesignDimensionsService.multiNumberToMultiNumber(value);
				}
				suffix = 'px'; // pixels
				dividerLine = true;
				break;

			case 'padding':
				ctype = 'pillbox_multi';
				index = '__padding__';
				if (prop.type === 'percentage' && false) { // NB && false => never!
					value = this.interactivityDesignDimensionsService.multiPercToMultiNumber(value);
				} else {
					value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				}
				suffix = 'px'; // pixels
				dividerLine = true;
				break;

			case 'textFormat/textFormat/leftMargin': // text margin left // DEPRECATED
			case 'textFormat/textFormat/rightMargin': // text margin right // DEPRECATED
				ctype = 'pillbox';
				index = '__margin__';
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = true;
				break;

			case 'toppadding': // text padding top // DEPRECATED
			case 'bottompadding': // text padding bottom // DEPRECATED
				ctype = 'pillbox';
				index = '__padding__';
				value = this.interactivityDesignDimensionsService.translateValueForInterface({ key, val: prop.value });
				suffix = 'px'; // pixels
				dividerLine = true;
				break;

			case 'cssclass':
			case 'text':
				ctype = 'pillbox';
				dividerLine = true;
				break;

			case 'useAsKPI':
			case 'showControls':
			case 'loop':
			case 'preload':
				ctype = 'checkbox';
				dividerLine = true;
				break;

			case 'value':
				ctype = 'none';
				dividerLine = true;
		}

		return {
			ctype, key, index, prefix, vtype: prop.type,
			value, suffix, dividerLine, filterSet, theme: 'dark-theme',
			showAspectRatioLock, aspectRatioLocked
		};
	}

	private getDefinitionId(type: WidgetType): string {
		const ret = '';
		for (let i = 0, l: number = this._widgetDefinitions.length; i < l; i++) {
			const widgetDefinition = this._widgetDefinitions[i];
			if (widgetDefinition.widgetDefinitionType === type) {
				return widgetDefinition.id;
			}
		}

		return ret;
	}

	private designDimensionsChanged(designDimensions: any) {
		this._designWidth = designDimensions.width;
		this._designHeight = designDimensions.height;
		this._stageAspectRatio = designDimensions.height / designDimensions.width;
	}

	private getDefaultConfig(type: WidgetType, stateName?: string): any {
		const config: any = {};
		for (let i = 0, l: number = this._widgetDefinitions.length; i < l; i++) {
			const widgetDefinition = this._widgetDefinitions[i];
			if (widgetDefinition.widgetDefinitionType === type) {
				if (stateName) {
					return widgetDefinition.stateDefaults?.[stateName] ? Object.assign({}, widgetDefinition.stateDefaults[stateName]) : {};
				}
				return Object.assign({}, widgetDefinition.configFields); // make pure new object
			}
		}

		return config;
	}
}
